import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { SceneObject, Evidence } from '../types';
import { getPersonColor } from '../utils/helpers';

interface TableViewProps {
  sceneObjects: SceneObject[];
  evidenceList: Evidence[];
  attributeFilters: any;
  attributeLock: any;
  currentCaseId: string;
  onObjectClick: (object: SceneObject | Evidence) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`table-tabpanel-${index}`}
      aria-labelledby={`table-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const TableView: React.FC<TableViewProps> = ({
  sceneObjects,
  evidenceList,
  attributeFilters,
  attributeLock,
  currentCaseId,
  onObjectClick
}) => {
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Filter objects based on current case
  const filteredSceneObjects = sceneObjects.filter(obj => obj.caseId === currentCaseId);
  const filteredEvidence = evidenceList.filter(evidence => evidence.caseId === currentCaseId);

  // Filter based on attribute filters
  const getFilteredObjects = () => {
    return filteredSceneObjects.filter(obj => {
      // Check people filter
      if (obj.people && obj.people.length > 0) {
        const hasVisiblePerson = obj.people.some(person => 
          attributeFilters.people[person] !== false
        );
        if (!hasVisiblePerson) return false;
      }

      // Check location filter
      const locationName = obj.location.global?.name || obj.location.local?.name;
      if (locationName && attributeFilters.location[locationName] === false) {
        return false;
      }

      // Check time filter
      const dateString = new Date(obj.time).toDateString();
      if (attributeFilters.time[dateString] === false) {
        return false;
      }

      return true;
    });
  };

  const getFilteredEvidence = () => {
    return filteredEvidence.filter(evidence => {
      // Check location filter
      const locationName = evidence.location.global?.name || evidence.location.local?.name;
      if (locationName && attributeFilters.location[locationName] === false) {
        return false;
      }

      // Check time filter
      const dateString = new Date(evidence.time).toDateString();
      if (attributeFilters.time[dateString] === false) {
        return false;
      }

      return true;
    });
  };

  const filteredObjects = getFilteredObjects();
  const filteredEvidenceList = getFilteredEvidence();

  const formatTime = (timeString: string) => {
    const date = new Date(timeString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getLocationDisplay = (location: any) => {
    if (location.global) {
      return `🌍 ${location.global.name} (${location.global.lat.toFixed(4)}, ${location.global.lng.toFixed(4)})`;
    } else if (location.local) {
      return `🏠 ${location.local.name} @ ${location.local.parentLocation} (${location.local.x}, ${location.local.y}, ${location.local.z})`;
    }
    return 'Unknown';
  };

  const getPersonDisplay = (people: string[]) => {
    return people.map(person => {
      const displayName = person.replace(/^(Detective|Victim|Person of Interest|Witness|Security Guard)\s+/, '');
      const color = getPersonColor(person, filteredObjects.flatMap(obj => obj.people || []));
      return (
        <Chip
          key={person}
          label={displayName}
          size="small"
          sx={{
            backgroundColor: color,
            color: 'white',
            fontSize: '0.7rem',
            height: '20px',
            mr: 0.5,
            mb: 0.5
          }}
        />
      );
    });
  };



  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
          <DescriptionIcon sx={{ mr: 1 }} />
          Data View
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredObjects.length} scene objects and {filteredEvidenceList.length} evidence items
        </Typography>
      </Box>

      <Divider />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="table tabs">
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                Scene Objects ({filteredObjects.length})
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CategoryIcon sx={{ mr: 1, fontSize: 16 }} />
                Evidence ({filteredEvidenceList.length})
              </Box>
            } 
          />

        </Tabs>
      </Box>

      {/* Scene Objects Tab */}
      <TabPanel value={tabValue} index={0}>
        <TableContainer component={Paper} sx={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>People</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredObjects.map((obj, index) => (
                <TableRow 
                  key={obj.id} 
                  hover 
                  onClick={() => onObjectClick(obj)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f8f9fa' } }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                      {obj.people && getPersonDisplay(obj.people)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={obj.category}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationIcon sx={{ mr: 0.5, fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {getLocationDisplay(obj.location)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ScheduleIcon sx={{ mr: 0.5, fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {formatTime(obj.time)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {obj.name}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Evidence Tab */}
      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper} sx={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEvidenceList.map((evidence) => (
                <TableRow 
                  key={evidence.id} 
                  hover 
                  onClick={() => onObjectClick(evidence)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f8f9fa' } }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        src={evidence.image} 
                        sx={{ width: 24, height: 24, mr: 1, fontSize: '0.7rem' }}
                      >
                        {evidence.name.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {evidence.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={evidence.type}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationIcon sx={{ mr: 0.5, fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {getLocationDisplay(evidence.location)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ScheduleIcon sx={{ mr: 0.5, fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {formatTime(evidence.time)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {evidence.source ? (
                      <Chip
                        label={evidence.source}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary', fontStyle: 'italic' }}>
                        Manual Entry
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {evidence.description}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
                 </TableContainer>
       </TabPanel>

       
     </Box>
   );
 };

export default TableView;
